import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface StudyProgram {
  id: string;
  client_id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  /** Master switch: may this program appear in an agent's context at all. */
  agent_context_enabled?: boolean;
  /**
   * Derive learner enrollments from the LMS course enrollment of this
   * program's curriculum courses instead of requiring assignment rules.
   */
  auto_enroll_from_courses?: boolean;
}

export interface StudyProgramInput {
  name: string;
  code?: string | null;
  description?: string | null;
  agent_context_enabled?: boolean;
  auto_enroll_from_courses?: boolean;
}

/** Dry run of the enrollment derivation — shown before the switch is flipped. */
export interface DerivablePreview {
  auto_enroll_from_courses: boolean;
  courses_linked: number;
  courses_with_lms_link: number;
  learners_derivable: number;
  learners_blocked_by_stronger_source: number;
}

export interface DeriveResult {
  enabled: boolean;
  added: number;
  updated: number;
  removed: number;
  target: number;
}

export interface LearningGoal {
  id: string;
  description: string;
  bloom_level?:
    | 'remember'
    | 'understand'
    | 'apply'
    | 'analyze'
    | 'evaluate'
    | 'create'
    | null;
  status?: string;
}

export interface CurriculumCoverage {
  courses_total: number;
  courses_covered: number;
  programs: {
    name: string | null;
    code: string | null;
    course_count: number;
    groups: string[];
  }[];
}

export interface ContentProposal {
  description: string | null;
  description_source: string | null;
  goals: LearningGoal[];
  goals_source: string | null;
}

export interface CurriculumItem {
  id: string;
  program_id: string;
  course_id: string;
  group_id?: string | null;
  semester: number;
  is_mandatory: boolean;
  learning_goals: LearningGoal[];
  custom_attributes?: Record<string, any>;
  display_order?: number | null;
  description?: string | null;
  description_source?: string | null;
  goals_source?: string | null;
  /** null = inherit from the group. */
  agent_context_enabled?: boolean | null;
}

export interface CurriculumItemInput {
  course_id: string;
  group_id?: string | null;
  semester: number;
  is_mandatory: boolean;
  learning_goals: LearningGoal[];
  custom_attributes?: Record<string, any>;
  display_order?: number | null;
  description?: string | null;
  /** manual | moodle | structure — set when the client accepts a proposal, so
   *  a later resync knows which values are hand-written. */
  description_source?: string | null;
  goals_source?: string | null;
  /** null = inherit the agent release from the group. */
  agent_context_enabled?: boolean | null;
}

export type AttributeType = 'text' | 'number' | 'dropdown' | 'boolean';

export interface CurriculumAttribute {
  id: string;
  program_id: string;
  label: string;
  attr_type: AttributeType;
  options: string[];
  display_order: number;
  is_required: boolean;
}

export interface CurriculumAttributeInput {
  label: string;
  attr_type: AttributeType;
  options?: string[];
  display_order?: number;
  is_required?: boolean;
}

export interface LearnerEnrollment {
  learner_user_id: string;
  program_id: string;
  start_semester?: number | null;
  current_semester?: number | null;
  source?: string;
}

export interface CurriculumGroup {
  id: string;
  program_id: string;
  parent_id?: string | null;
  name: string;
  group_type?: string | null;
  display_order: number;
  source_type: 'manual' | 'moodle_category' | 'course_bucket';
  source_ref?: string | null;
  learning_goals: LearningGoal[];
  is_ordered?: boolean;
  /** null = inherit from the parent node. */
  agent_context_enabled?: boolean | null;
}

export interface CurriculumGroupInput {
  parent_id?: string | null;
  name: string;
  group_type?: string | null;
  /** True = the course order in this group is a sequence, not a sort order. */
  is_ordered?: boolean;
  /** null = inherit from the parent node. */
  agent_context_enabled?: boolean | null;
  display_order?: number;
  source_type?: 'manual' | 'moodle_category' | 'course_bucket';
  source_ref?: string | null;
  learning_goals?: LearningGoal[];
}

export interface CurriculumTreeNode extends CurriculumGroup {
  items: CurriculumItem[];
  children: CurriculumTreeNode[];
}

export interface CurriculumTree {
  program_id: string;
  /** Effective floor for every node that inherits. */
  agent_context_enabled?: boolean;
  groups: CurriculumTreeNode[];
  ungrouped: CurriculumItem[];
}

export interface AssignmentRule {
  id: string;
  program_id: string;
  rule_type: 'moodle_category' | 'course_bucket' | 'manual';
  ref_key?: string | null;
  default_start_semester?: number | null;
  is_active: boolean;
}

export interface AssignmentRuleInput {
  rule_type: 'moodle_category' | 'course_bucket' | 'manual';
  ref_key?: string | null;
  default_start_semester?: number | null;
  is_active?: boolean;
}

export interface MoodleCourseOption {
  canonical_id: string;
  course_id: string | null; // null = not yet mirrored/synced
  name: string;
  moodle_course_id: number;
}

export interface CourseResponsible {
  id: string;
  user_id: string;
  name?: string | null;
  email?: string | null;
  /** 'lms_role' = aus der Moodle-Lehrrolle materialisiert, 'granted' = benannt. */
  source: string;
  granted_by?: string | null;
  created_at?: string | null;
}

export interface CourseResponsibles {
  course_id: string;
  responsibles: CourseResponsible[];
  /** false heisst: fuer diesen Kurs kann keine Verknuepfung beantragt werden. */
  has_responsible: boolean;
  /** true, wenn der Aufrufer hier benennen oder entziehen darf. */
  may_appoint: boolean;
}

export interface MoodleCategoryGroup {
  key: string; // "{registration_id}:{category_id}" or "uncategorized"
  category_id: number | null;
  name: string | null;
  path: string | null;
  label: string;
  count: number;
  courses: MoodleCourseOption[];
}

@Injectable({
  providedIn: 'root',
})
export class CurriculumService {
  private baseUrl = `${environment.tutorsBackend}`;

  programs$ = new BehaviorSubject<StudyProgram[]>([]);
  items$ = new BehaviorSubject<CurriculumItem[]>([]);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.reset());
  }

  listPrograms() {
    return this.http.get(`${this.baseUrl}/study-programs`).pipe(
      map((d: any) => d.data as StudyProgram[]),
      tap((programs) => this.programs$.next(programs))
    );
  }

  createProgram(input: StudyProgramInput) {
    return this.http.post(`${this.baseUrl}/study-programs`, input).pipe(
      map((p: any) => p as StudyProgram),
      tap((program) => this.programs$.next([...this.programs$.value, program]))
    );
  }

  updateProgram(id: string, input: StudyProgramInput) {
    return this.http.patch(`${this.baseUrl}/study-programs/${id}`, input).pipe(
      map((p: any) => p as StudyProgram),
      tap((program) =>
        this.programs$.next(
          this.programs$.value.map((p) => (p.id === id ? program : p))
        )
      )
    );
  }

  deleteProgram(id: string) {
    return this.http.delete(`${this.baseUrl}/study-programs/${id}`).pipe(
      tap(() =>
        this.programs$.next(this.programs$.value.filter((p) => p.id !== id))
      )
    );
  }

  listItems(program_id: string) {
    return this.http
      .get(`${this.baseUrl}/study-programs/${program_id}/items`)
      .pipe(
        map((d: any) => d.data as CurriculumItem[]),
        tap((items) => this.items$.next(items))
      );
  }

  createItem(program_id: string, input: CurriculumItemInput) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/items`, input)
      .pipe(
        map((i: any) => i as CurriculumItem),
        tap((item) => this.items$.next([...this.items$.value, item]))
      );
  }

  updateItem(id: string, input: CurriculumItemInput) {
    return this.http.patch(`${this.baseUrl}/curriculum-items/${id}`, input).pipe(
      map((i: any) => i as CurriculumItem),
      tap((item) =>
        this.items$.next(
          this.items$.value.map((i) => (i.id === id ? item : i))
        )
      )
    );
  }

  deleteItem(id: string) {
    return this.http.delete(`${this.baseUrl}/curriculum-items/${id}`).pipe(
      tap(() =>
        this.items$.next(this.items$.value.filter((i) => i.id !== id))
      )
    );
  }

  getEnrollment(learner_user_id: string) {
    return this.http
      .get(`${this.baseUrl}/learner/${learner_user_id}/enrollment`)
      .pipe(map((e: any) => e as LearnerEnrollment | null));
  }

  listEnrollments(learner_user_id: string) {
    return this.http
      .get(`${this.baseUrl}/learner/${learner_user_id}/enrollments`)
      .pipe(map((e: any) => e as LearnerEnrollment[]));
  }

  upsertEnrollment(learner_user_id: string, input: Omit<LearnerEnrollment, 'learner_user_id' | 'source'>) {
    return this.http
      .put(`${this.baseUrl}/learner/${learner_user_id}/enrollment`, input)
      .pipe(map((e: any) => e as LearnerEnrollment));
  }

  deleteEnrollment(learner_user_id: string, program_id: string) {
    return this.http.delete(
      `${this.baseUrl}/learner/${learner_user_id}/enrollment/${program_id}`
    );
  }

  // --- Tree + groups ---

  getTree(program_id: string) {
    return this.http
      .get(`${this.baseUrl}/study-programs/${program_id}/tree`)
      .pipe(map((t: any) => t as CurriculumTree));
  }

  createGroup(program_id: string, input: CurriculumGroupInput) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/groups`, input)
      .pipe(map((g: any) => g as CurriculumGroup));
  }

  updateGroup(group_id: string, input: CurriculumGroupInput) {
    return this.http
      .patch(`${this.baseUrl}/curriculum-groups/${group_id}`, input)
      .pipe(map((g: any) => g as CurriculumGroup));
  }

  deleteGroup(group_id: string) {
    return this.http.delete(`${this.baseUrl}/curriculum-groups/${group_id}`);
  }

  expandGroup(group_id: string) {
    return this.http
      .post(`${this.baseUrl}/curriculum-groups/${group_id}/expand`, {})
      .pipe(map((r: any) => r as { added: number; skipped: number }));
  }

  // --- Assignment rules ---

  listRules(program_id: string) {
    return this.http
      .get(`${this.baseUrl}/study-programs/${program_id}/rules`)
      .pipe(map((r: any) => r as AssignmentRule[]));
  }

  createRule(program_id: string, input: AssignmentRuleInput) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/rules`, input)
      .pipe(map((r: any) => r as AssignmentRule));
  }

  deleteRule(rule_id: string) {
    return this.http.delete(`${this.baseUrl}/curriculum-rules/${rule_id}`);
  }

  applyRules(program_id: string) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/apply-rules`, {})
      .pipe(map((r: any) => r as { added: number; removed: number; target: number }));
  }

  // --- Verantwortliche eines Kurses ---------------------------------------
  // Wer fuer das Material eines Kurses einsteht. Zwei Akte, die man nicht
  // verwechseln darf: hier wird jemand BENANNT (ein Erteilender genuegt),
  // die inhaltliche Verknuepfung bestaetigen muessen dagegen alle
  // Verantwortlichen BEIDER Kurse.

  listResponsibles(course_id: string) {
    return this.http
      .get(`${this.baseUrl}/courses/${course_id}/responsibles`)
      .pipe(map((r: any) => r as CourseResponsibles));
  }

  grantResponsible(course_id: string, user_id: string) {
    return this.http.post(
      `${this.baseUrl}/courses/${course_id}/responsibles`,
      { user_id }
    );
  }

  revokeResponsible(course_id: string, user_id: string) {
    return this.http.delete(
      `${this.baseUrl}/courses/${course_id}/responsibles/${user_id}`
    );
  }

  /**
   * Verantwortlichkeit aus den LMS-Lehrrollen materialisieren.
   *
   * Liefert {candidates, matched, created}: `candidates - matched` ist die
   * Zahl der Lehrenden, die das LMS kennt und wir nicht erreichen — ohne
   * PII-Freigabe speichert der Sync keine E-Mail, und genau diese Differenz
   * erklaert eine sonst leere Liste.
   */
  syncLmsResponsibles() {
    return this.http
      .post(`${this.baseUrl}/curriculum/responsibles/sync-lms`, {})
      .pipe(map((r: any) => r as { candidates: number; matched: number; created: number }));
  }

  /**
   * May the current user change this course's assignments? Answered by the
   * placements endpoint, which applies the one rule: manager, admin, or the
   * course's responsible person. Asking beforehand keeps a button from
   * appearing only to fail with a 403.
   */
  mayEditCourse(course_id: string) {
    return this.http
      .get(`${this.baseUrl}/placements/courses/${course_id}`)
      .pipe(map((r: any) => !!r?.data?.may_edit));
  }

  /** How many learners the derivation would cover. Writes nothing. */
  derivableLearners(program_id: string) {
    return this.http
      .get(`${this.baseUrl}/study-programs/${program_id}/derivable-learners`)
      .pipe(map((r: any) => r as DerivablePreview));
  }

  deriveEnrollments(program_id: string) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/derive-enrollments`, {})
      .pipe(map((r: any) => r as DeriveResult));
  }

  // --- LLM import (upload+poll on al-filemanager, commit on al-tutor) ---

  uploadImport(file: File, language?: string) {
    const form = new FormData();
    form.append('file', file);
    if (language) form.append('language', language);
    return this.http
      .post(`${environment.filesBackend}/curriculum/parse`, form)
      .pipe(map((r: any) => r as { job_id: string }));
  }

  getImportStatus(job_id: string) {
    return this.http
      .get(`${environment.filesBackend}/curriculum/parse/${job_id}`)
      .pipe(
        map(
          (r: any) =>
            r as {
              job_id: string;
              status: 'queued' | 'processing' | 'done' | 'error';
              filename: string;
              parsed_curriculum: any;
              warnings: string[];
              error_code?: string | null;
              error_message?: string | null;
            }
        )
      );
  }

  // --- Custom attributes (per-program columns) ---

  listAttributes(program_id: string) {
    return this.http
      .get(`${this.baseUrl}/study-programs/${program_id}/attributes`)
      .pipe(map((a: any) => a as CurriculumAttribute[]));
  }

  createAttribute(program_id: string, input: CurriculumAttributeInput) {
    return this.http
      .post(`${this.baseUrl}/study-programs/${program_id}/attributes`, input)
      .pipe(map((a: any) => a as CurriculumAttribute));
  }

  updateAttribute(id: string, input: CurriculumAttributeInput) {
    return this.http
      .patch(`${this.baseUrl}/curriculum-attributes/${id}`, input)
      .pipe(map((a: any) => a as CurriculumAttribute));
  }

  deleteAttribute(id: string) {
    return this.http.delete(`${this.baseUrl}/curriculum-attributes/${id}`);
  }

  // --- Moodle-course-driven creation ---

  listMoodleCourses() {
    return this.http
      .get(`${this.baseUrl}/curriculum/moodle-courses`)
      .pipe(map((r: any) => (r.categories || []) as MoodleCategoryGroup[]));
  }

  bulkFromCategories(program_prefixes: string[], create_rules: boolean) {
    return this.http
      .post(`${this.baseUrl}/curriculum/bulk-from-categories`, { program_prefixes, create_rules })
      .pipe(
        map(
          (r: any) =>
            r as {
              programs_created: number;
              groups_created: number;
              items_created: number;
              rules_created: number;
              programs: { program_id: string; name: string }[];
            }
        )
      );
  }

  /** `module_depth` (1-based) types the groups on that level of the category
   *  path as modules. Null adopts the hierarchy without typing it. */
  createFromCategories(
    name: string,
    code: string | null,
    category_keys: string[],
    module_depth: number | null = null
  ) {
    return this.http
      .post(`${this.baseUrl}/curriculum/from-categories`, {
        name,
        code,
        category_keys,
        module_depth,
      })
      .pipe(
        map(
          (r: any) =>
            r as {
              program_id: string;
              groups_created: number;
              courses_created: number;
              warnings: string[];
            }
        )
      );
  }

  commitImport(parsed: any) {
    return this.http
      .post(`${this.baseUrl}/curriculum/import/commit`, parsed)
      .pipe(
        map(
          (r: any) =>
            r as {
              program_id: string;
              groups_created: number;
              courses_created: number;
              warnings: string[];
            }
        )
      );
  }

  private reset() {
    this.programs$.next([]);
    this.items$.next([]);
  }
  /** What this course could adopt from the LMS — description and learning
   *  goals. A proposal, not a write: the user reviews it before saving, so a
   *  sync can never overwrite hand-written curriculum text. */
  contentProposal(itemId: string): Observable<ContentProposal> {
    return this.http.get<ContentProposal>(
      `${environment.tutorsBackend}/curriculum-items/${itemId}/content-proposal`
    );
  }
  /** Move several courses into one group (or out of every group with null).
   *  Building a module group by hand meant opening one row after another. */
  bulkMoveItems(itemIds: string[], groupId: string | null) {
    return this.http.post<{ moved: number }>(
      `${environment.tutorsBackend}/curriculum-items/bulk-move`,
      { item_ids: itemIds, group_id: groupId }
    );
  }
  /** What the agent's curriculum switch actually reaches: how many of the
   *  courses it answers in sit in a released curriculum. */
  botCurriculumCoverage(botId: string) {
    return this.http.get<CurriculumCoverage>(
      `${environment.tutorsBackend}/tutor/${botId}/curriculum-coverage`
    );
  }

  /** Which agents would read this programme — placed in one of its courses
   *  and carrying the switch. */
  programAgentUsage(programId: string) {
    return this.http.get<{ bots: { id: string; name: string }[] }>(
      `${environment.tutorsBackend}/study-programs/${programId}/agent-usage`
    );
  }
}
